import { AfterViewInit, Directive, effect, ElementRef, inject, input, InputSignal, OnDestroy } from '@angular/core'

@Directive({
	selector: '[appAnimatedNumber]',
})
export class AnimatedNumberDirective implements AfterViewInit, OnDestroy {
	private static readonly DURATION: number = 150
	private static readonly TRANSITION: string = 'transform 150ms cubic-bezier(0.16, 1, 0.3, 1)'
	private static readonly TRANSFORMS: string[] = Array.from(
		{ length: 10 },
		(_, i: number): string => `translateY(${-i}em)`,
	)
	private static readonly DIGIT_TEXT: string = '0\n1\n2\n3\n4\n5\n6\n7\n8\n9'
	private static _stripTemplate: HTMLElement | null = null

	public appAnimatedNumber: InputSignal<string | number | null | undefined> = input<
		string | number | null | undefined
	>()

	private readonly _el: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef)

	private _containerEl: HTMLElement | null = null
	private _cols: HTMLElement[] = []
	private _strips: (HTMLElement | null)[] = []
	private _chars: string[] = []
	private _isDigit: boolean[] = []
	private _initialized: boolean = false
	private _cleanupTimer: ReturnType<typeof setTimeout> | null = null
	private _rafId: number = 0
	private _lastText: string = ''
	private _pendingText: string | null = null

	constructor() {
		effect((): void => {
			const value: string | number | null | undefined = this.appAnimatedNumber()
			const text: string = (value ?? '').toString().trim()
			if (!this._containerEl || !text || text === this._lastText) return
			this._lastText = text
			this._scheduleRender(text)
		})
	}

	private static _getStripTemplate(): HTMLElement {
		if (!this._stripTemplate) {
			const strip: HTMLElement = document.createElement('span')
			strip.className = 'animated-number-strip'
			strip.textContent = this.DIGIT_TEXT
			this._stripTemplate = strip
		}
		return this._stripTemplate
	}

	private _scheduleRender(text: string): void {
		this._pendingText = text
		if (!this._rafId) {
			this._rafId = requestAnimationFrame((): void => {
				this._rafId = 0
				if (this._pendingText) {
					this._render(this._pendingText)
					this._pendingText = null
				}
			})
		}
	}

	private _buildDigitColumn(digit: number): HTMLElement {
		const col: HTMLElement = document.createElement('span')
		col.className = 'animated-number-col'

		const strip: HTMLElement = AnimatedNumberDirective._getStripTemplate().cloneNode(true) as HTMLElement
		strip.style.transform = AnimatedNumberDirective.TRANSFORMS[digit] ?? ''
		col.appendChild(strip)

		return col
	}

	private _buildCharColumn(char: string): HTMLElement {
		const el: HTMLElement = document.createElement('span')
		el.className = 'animated-number-char'
		el.textContent = char === ' ' || char === '\u00A0' ? '\u00A0' : char
		return el
	}

	private _scheduleTransitionCleanup(): void {
		if (this._cleanupTimer) return

		this._cleanupTimer = setTimeout((): void => {
			for (let i: number = 0; i < this._strips.length; i++) {
				const strip: HTMLElement | null = this._strips[i] ?? null
				if (strip) {
					strip.style.transition = ''
					strip.style.willChange = 'auto'
				}
			}
			this._cleanupTimer = null
		}, AnimatedNumberDirective.DURATION + 50)
	}

	private _render(text: string): void {
		const container: HTMLElement | null = this._containerEl
		if (!container) return

		const len: number = text.length
		const prevLen: number = this._chars.length

		if (len === prevLen && this._initialized) {
			let inPlace: boolean = true

			for (let i: number = 0; i < len; i++) {
				const char: string | undefined = text[i]
				if (!char) break

				const isDigit: boolean = char >= '0' && char <= '9'
				const strip: HTMLElement | null | undefined = this._strips[i]

				if (isDigit && this._isDigit[i] && strip) {
					if (char !== this._chars[i]) {
						strip.style.willChange = 'transform'
						strip.style.transition = AnimatedNumberDirective.TRANSITION
						strip.style.transform = AnimatedNumberDirective.TRANSFORMS[+char] ?? ''
						this._chars[i] = char
					}
				} else if (isDigit !== this._isDigit[i] || char !== this._chars[i]) {
					inPlace = false
					break
				}
			}

			if (inPlace) {
				this._scheduleTransitionCleanup()
				return
			}
		}

		for (let i: number = prevLen - 1; i >= 0; i--) {
			const oldCol: HTMLElement | undefined = this._cols[i]
			if (oldCol) container.removeChild(oldCol)
		}

		this._cols.length = len
		this._strips.length = len
		this._chars.length = len
		this._isDigit.length = len

		for (let i: number = 0; i < len; i++) {
			const char: string | undefined = text[i]
			if (!char) break

			const isDigit: boolean = char >= '0' && char <= '9'
			let col: HTMLElement

			if (isDigit) {
				col = this._buildDigitColumn(+char)
				this._strips[i] = (col.firstChild as HTMLElement) ?? null
			} else {
				col = this._buildCharColumn(char)
				this._strips[i] = null
			}

			this._cols[i] = col
			this._chars[i] = char
			this._isDigit[i] = isDigit
			container.appendChild(col)
		}

		this._initialized = true
	}

	ngAfterViewInit(): void {
		const container: HTMLElement = document.createElement('span')
		container.className = 'animated-number'
		this._el.nativeElement.appendChild(container)
		this._containerEl = container

		const value: string | number | null | undefined = this.appAnimatedNumber()
		const text: string = (value ?? '').toString().trim()
		if (text) {
			this._lastText = text
			this._render(text)
		}
	}

	ngOnDestroy(): void {
		if (this._rafId) cancelAnimationFrame(this._rafId)
		if (this._cleanupTimer) clearTimeout(this._cleanupTimer)
		this._cols.length = 0
		this._strips.length = 0
		this._chars.length = 0
		this._isDigit.length = 0
		this._pendingText = null
		if (this._containerEl) {
			this._el.nativeElement.removeChild(this._containerEl)
			this._containerEl = null
		}
	}
}
